import { useMemo } from 'react';
import { bindActionCreators } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import type { IRootState } from '@store/slices';
import type { AppDispatch } from '@store/store';
import type { ISchedulerData, ISchedulerFilters, ISchedulerTask, TaskId } from '@interfaces/IScheduler';
import { schedulerActions, undoAction, redoAction, clearHistoryAction, revertLastAction } from '@store/slices/scheduler.slice';
import { composeScheduleData, composeTaskView } from '@components/Pages/SchedulerPage/lib/taskViewCodec';
import type { CascadeFreeze } from '@components/Pages/SchedulerPage/lib/freeze';
import { rangeStretch } from '@components/Pages/SchedulerPage/lib/timeScale';

/**
 * Shallow equality for `colorPreview` (Record<string,string> | null). Prevents
 * SchedulerCanvas from re-rendering when setColorPreview is called with an
 * object that has the same keys and values — e.g. repeated slider ticks at the
 * same stop, or a React 19 double-invoke of the transition in StrictMode.
 */
function colorPreviewEqual(
  a: Record<string, string> | null,
  b: Record<string, string> | null,
): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

/**
 * A site's full schedule as `TaskView`s — the read-time JOIN of the plan facet
 * (`bySite`) and the execution facet (`activityBySite`). Composition is
 * memoised on the two source references, so it only recomputes when this site's
 * plan or execution actually changes (Immer preserves the identity of unmutated
 * site slices). Components keep reading `task.progressPercent` / `.status` /
 * `.locations` / `.log` off the returned tasks exactly as before the split.
 */
export function useSchedulerSite(siteId: string): ISchedulerData | undefined {
  const plan = useSelector((state: IRootState) => state.scheduler.bySite[siteId]);
  const execution = useSelector((state: IRootState) => state.scheduler.activityBySite[siteId]);
  return useMemo(() => (plan ? composeScheduleData(plan, execution) : undefined), [plan, execution]);
}

/**
 * ONE task by id, composed into a `TaskView` — narrower than `useSchedulerSite`,
 * so consumers only re-render when that task's plan OR execution identity
 * changes, not on every mutation anywhere in the site (Immer re-references the
 * site root on any edit).
 */
export function useSchedulerTask(siteId: string, taskId: string): ISchedulerTask | undefined {
  const planTask = useSelector((state: IRootState) =>
    state.scheduler.bySite[siteId]?.tasks.find((task) => task.id === taskId),
  );
  const execution = useSelector((state: IRootState) => state.scheduler.activityBySite[siteId]?.[taskId]);
  return useMemo(
    () => (planTask ? composeTaskView(planTask, execution) : undefined),
    [planTask, execution],
  );
}

/** True when the site has Genba-syncable edits not yet saved. */
export function useSchedulerDirty(siteId: string) {
  return useSelector((state: IRootState) => state.scheduler.dirtyBySite[siteId] ?? false);
}

/** True when the loaded schedule is behind the upstream version (edits frozen). */
export function useSchedulerStale(siteId: string) {
  return useSelector((state: IRootState) => state.scheduler.staleBySite[siteId] ?? false);
}

/** The site's observed-work anchors, in the shape `cascade` takes — empty on a
 *  page that does not mirror the activity feed. */
export function useSchedulerFrozenTasks(siteId: string): ReadonlyMap<TaskId, CascadeFreeze> {
  const frozen = useSelector((state: IRootState) => state.scheduler.frozenBySite[siteId]);
  return useMemo(() => new Map(Object.entries(frozen ?? {})), [frozen]);
}

export function useSchedulerZoom() {
  return useSelector((state: IRootState) => state.scheduler.ui.zoom);
}
export function useSchedulerZoomScale() {
  return useSelector((state: IRootState) => state.scheduler.ui.zoomScale);
}
/**
 * The horizontal scale — px per day = PX_PER_DAY[zoom] × this. The geometric
 * zoom, stretched when a short date range would leave the canvas part-empty
 * (`rangeStretch`). Everything that maps dates ↔ x reads this; row heights,
 * text and sizes read `useSchedulerZoomScale`.
 */
export function useSchedulerTimeScale() {
  return useSelector((state: IRootState) => {
    const { zoom, zoomScale, viewRange, canvasWidthPx } = state.scheduler.ui;
    return zoomScale * rangeStretch(viewRange, zoom, zoomScale, canvasWidthPx);
  });
}
/** Scheduler-wide milestone diamond colour (set by the auto-colorize panel). */
export function useSchedulerMilestoneColor() {
  return useSelector((state: IRootState) => state.scheduler.ui.milestoneColor);
}
/** Live auto-colorize preview map (null when no preview is active). */
export function useSchedulerColorPreview() {
  return useSelector((state: IRootState) => state.scheduler.ui.colorPreview, colorPreviewEqual);
}
/** Whether the in-bar progress (PPC) band is shown on task bars. */
export function useSchedulerShowBarProgress() {
  return useSelector((state: IRootState) => state.scheduler.ui.showBarProgress);
}
/** Whether group/summary headers pin (stacked) to the top while scrolling. */
export function useSchedulerStickyGroups() {
  return useSelector((state: IRootState) => state.scheduler.ui.stickyGroups);
}
/** Whether bar labels slide along their bars to follow the horizontal scroll. */
export function useSchedulerStickyLabels() {
  return useSelector((state: IRootState) => state.scheduler.ui.stickyLabels);
}
export function useSchedulerShowCriticalPath() {
  return useSelector((state: IRootState) => state.scheduler.ui.showCriticalPath);
}
export function useSchedulerSelectedTaskId() {
  return useSelector((state: IRootState) => state.scheduler.ui.selectedTaskId);
}
export function useSchedulerSelectedDependencyId() {
  return useSelector((state: IRootState) => state.scheduler.ui.selectedDependencyId);
}
/**
 * Content equality for the filter object (order-stable `status` / `groupIds`
 * arrays). Immer already preserves `ui.filters` identity across unrelated
 * actions, so this only matters when `setFilters` is dispatched with the same
 * values — it then avoids re-running the filteredSite → geometry chain.
 */
function filtersEqual(a: ISchedulerFilters, b: ISchedulerFilters): boolean {
  if (a === b) return true;
  if (a.status.length !== b.status.length || a.groupIds.length !== b.groupIds.length) return false;
  for (let i = 0; i < a.status.length; i++) if (a.status[i] !== b.status[i]) return false;
  for (let i = 0; i < a.groupIds.length; i++) if (a.groupIds[i] !== b.groupIds[i]) return false;
  return true;
}
export function useSchedulerFilters() {
  return useSelector((state: IRootState) => state.scheduler.ui.filters, filtersEqual);
}
export function useSchedulerSearch() {
  return useSelector((state: IRootState) => state.scheduler.ui.search);
}
export function useSchedulerViewRange() {
  return useSelector((state: IRootState) => state.scheduler.ui.viewRange);
}
export function useSchedulerPanelOpen() {
  return useSelector((state: IRootState) => state.scheduler.ui.panelOpen);
}
/** The task whose commit review is open, or null (see `commitTaskId`). */
export function useSchedulerCommitTaskId() {
  return useSelector((state: IRootState) => state.scheduler.ui.commitTaskId);
}
/** The row deletion awaiting confirmation, or null (see `pendingDelete`). */
export function useSchedulerPendingDelete() {
  return useSelector((state: IRootState) => state.scheduler.ui.pendingDelete);
}
export function useSchedulerPanelTab() {
  return useSelector((state: IRootState) => state.scheduler.ui.panelTab);
}
export function useSchedulerLastError() {
  return useSelector((state: IRootState) => state.scheduler.ui.lastError);
}
export function useSchedulerClipboardTask(siteId: string): ISchedulerTask | null {
  const planTask = useSelector((state: IRootState) => {
    const id = state.scheduler.ui.clipboardTaskId;
    return id ? (state.scheduler.bySite[siteId]?.tasks.find(t => t.id === id) ?? null) : null;
  });
  const execution = useSelector((state: IRootState) => {
    const id = state.scheduler.ui.clipboardTaskId;
    return id ? state.scheduler.activityBySite[siteId]?.[id] : undefined;
  });
  // Compose so a paste duplicates the source's execution facet too (status,
  // progress, areas) — the clipboard holds a full TaskView by id.
  return useMemo(
    () => (planTask ? composeTaskView(planTask, execution) : null),
    [planTask, execution],
  );
}

export function useSchedulerCanUndo() {
  return useSelector((state: IRootState) => state.scheduler.history.past.length > 0);
}

export function useSchedulerCanRedo() {
  return useSelector((state: IRootState) => state.scheduler.history.future.length > 0);
}

/**
 * Bound dispatchers for every scheduler reducer action plus the four
 * standalone history actions. Consumers destructure individual methods
 * and place them in effect / useMemo dep arrays (e.g.
 * `useDragReschedule`), so the returned object MUST be referentially
 * stable across renders.
 *
 * The single `useMemo([dispatch])` block (instead of one `useCallback`
 * per action) is required to keep the React Compiler ESLint analyzer
 * happy — manually memoizing 30+ inline closures one-by-one is flagged
 * as `react-hooks/preserve-manual-memoization`.
 *
 * No-payload actions are wrapped to `() => void` instead of left as
 * RTK's `ActionCreatorWithoutPayload<...>` — the latter carries a
 * `(noArgument: void)` signature that breaks assignment to event
 * handlers like `onClick`.
 */
export function useSchedulerActions() {
  const dispatch = useDispatch<AppDispatch>();
  return useMemo(() => {
    const bound = bindActionCreators(schedulerActions, dispatch);
    return {
      ...bound,
      closePanel: () => bound.closePanel(),
      closeCommitDialog: () => bound.closeCommitDialog(),
      cancelDelete: () => bound.cancelDelete(),
      clearError: () => bound.clearError(),
      clearViewFilters: () => bound.clearViewFilters(),
      zoomIn: () => bound.zoomIn(),
      zoomOut: () => bound.zoomOut(),
      zoomReset: () => bound.zoomReset(),
      undo: () => dispatch(undoAction()),
      redo: () => dispatch(redoAction()),
      clearHistory: () => dispatch(clearHistoryAction()),
      revertLast: () => dispatch(revertLastAction()),
    };
  }, [dispatch]);
}
